<script setup>
import { ref, onMounted } from "vue";
import moment from "moment";
import InfoHolder from "@/components/global/drawer/InfoHolder.vue";
import { useAcademyStore } from "@/stores";
import { useRoute, useRouter } from "vue-router";

const course = ref({});

const route = useRoute();
const router = useRouter();

const academyStore = useAcademyStore();

const goToEdit = () => {
  router.push("/academy/edit/" + route.params.id);
};

const formatDate = (date) => {
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("DD-MM-YYYY HH:mm");
};

const removeTimestamp = (filename) => {
  const underscoreIndex = filename.indexOf('_');
  return underscoreIndex !== -1 ? filename.substring(underscoreIndex + 1) : filename;
};

async function fetchCourseFile(file) {
  const res = await academyStore.fetchCourseFile(file.fileName);

  const blob = new Blob([res.data]);
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;

  // Use content-disposition header if filename is provided
  const disposition = res.headers["content-disposition"];
  const suggestedFilename =
    disposition?.split("filename=")[1]?.replace(/["']/g, "") ||
    removeTimestamp(file.fileName)
  ;

  link.setAttribute("download", suggestedFilename);
  document.body.appendChild(link);
  link.click();

  // Clean up
  link.remove();
  window.URL.revokeObjectURL(url);
}

onMounted(async () => {
  const data = await academyStore.getCourse({ id: route.params.id });
  course.value = data;
});
</script>

<template>
  <div v-if="Object.keys(course).length > 0" class="pa-5">
    <div class="d-flex align-center justify-space-between">
      <div>
        <h1>{{ course.cycle }}</h1>
      </div>
      <div>
        <v-btn size="small" color="academy" class="mr-5" @click="goToEdit">
          <v-icon>mdi-pencil</v-icon> edit course
        </v-btn>
      </div>
    </div>
    <v-divider />
    <div class="d-flex box-holder">
      <div class="d-flex box-row flex-1-0-0">
        <div class="box-container mt-5">
          <h3 class="pa-2"><v-icon>mdi-school</v-icon> Course Data</h3>
          <InfoHolder title="Training Cycle" :info="course.cycle" />

          <InfoHolder
            title="Training Course"
            :info="course.courseSection?.name"
          />

          <InfoHolder title="Thematic Area" :info="course.thematicArea?.name" />

          <InfoHolder title="Type" :info="course.courseType?.name" />

          <InfoHolder
            title="Status"
            :info="course.status?.name ? course.status?.name : '-'"
          />

          <InfoHolder
            title="Odyssea Projects"
            :info="course.projects?.map((p) => p.name).join(', ') || '—'"
          />

          <InfoHolder title="Odyssea Branch" :info="course.branch?.name" />

          <InfoHolder title="Start Date" :info="formatDate(course.startDate)" />

          <InfoHolder title="End Date" :info="formatDate(course.endDate)" />

          <InfoHolder title="Hours" :info="course.duration" />

          <InfoHolder
            title="Location"
            :info="course.location ? course.location : '-'"
          />

          <InfoHolder title="Tutor" :info="course.tutor ? course.tutor : '-'" />

          <div v-if="course.studentEvaluations?.length > 0" class="info pa-2">
            <p>
              <span class="label mb-1">Evaluations by Tutor</span>
              <span class="answer">
                <div
                  v-for="evaluation in course.studentEvaluations"
                  :key="evaluation.id"
                  class="mt-1 mb-1 d-inline"
                >
                  <router-link
                    :to="'/academy/evaluation/student/view/' + evaluation.id"
                    target="_blank"
                    class="chip-link-color-academy"
                  >
                    <v-chip> {{ evaluation.studentEvaluationId }} </v-chip>
                  </router-link>
                </div>
              </span>
            </p>
          </div>

          <div v-if="course.tutorEvaluations?.length > 0" class="info pa-2">
            <p>
              <span class="label mb-1">Evaluations by Students</span>
              <span class="answer">
                <div
                  v-for="evaluation in course.tutorEvaluations"
                  :key="evaluation.id"
                  class="mt-1 mb-1 d-inline"
                >
                  <router-link
                    :to="'/academy/evaluation/course/view/' + evaluation.id"
                    target="_blank"
                    class="chip-link-color-academy"
                  >
                    <v-chip> {{ evaluation.evaluationId }} </v-chip>
                  </router-link>
                </div>
              </span>
            </p>
          </div>
        </div>
        <div class="d-flex" style="gap: 10px">
          <div class="box-container box-w3 mt-5">
            <h3 class="pa-2 d-flex justify-space-between align-center">
              <div>
                <v-icon>mdi-account-school-outline</v-icon> Pre-Registered
                Beneficiaries
              </div>
              <div
                class="total-badge rounded d-flex align-center justify-center"
              >
                {{ course.preregisteredBeneficiaries.length }}
              </div>
            </h3>

            <div
              v-for="preben in course.preregisteredBeneficiaries"
              :key="preben.id"
              class="pa-2"
            >
              <router-link
                :to="'/beneficiary/intake/view/' + preben.id"
                target="_blank"
                class="chip-link-color"
              >
                <v-chip> {{ preben.name }} </v-chip>
              </router-link>
              <!-- <v-chip @click="handleBeneficiaryClick(preben.id, $event)">
                {{ preben.name }}
              </v-chip> -->
            </div>
          </div>

          <div class="box-container box-w3 mt-5">
            <h3 class="pa-2 d-flex justify-space-between align-center">
              <div>
                <v-icon>mdi-account-school</v-icon> Registered Beneficiaries
              </div>
              <div
                class="total-badge rounded d-flex align-center justify-center"
              >
                {{ course.registeredBeneficiaries.length }}
              </div>
            </h3>
            <div
              v-for="reben in course.registeredBeneficiaries"
              :key="reben.id"
              class="pa-2"
            >
              <router-link
                :to="'/beneficiary/intake/view/' + reben.id"
                target="_blank"
                class="chip-link-color"
              >
                <v-chip> {{ reben.name }} </v-chip>
              </router-link>
              <!-- <v-chip @click="handleBeneficiaryClick(reben.id, $event)">
                {{ reben.name }}
              </v-chip> -->
            </div>
          </div>

          <div class="box-container box-w3 mt-5">
            <h3 class="pa-2 d-flex justify-space-between align-center">
              <div><v-icon>mdi-star</v-icon> Completed Beneficiaries</div>
              <div
                class="total-badge rounded d-flex align-center justify-center"
              >
                {{ course.completedBeneficiaries.length }}
              </div>
            </h3>

            <div
              v-for="coben in course.completedBeneficiaries"
              :key="coben.id"
              class="pa-2"
            >
              <router-link
                :to="'/beneficiary/intake/view/' + coben.id"
                target="_blank"
                class="chip-link-color"
              >
                <v-chip> {{ coben.name }} </v-chip>
              </router-link>
              <!-- <v-chip @click="handleBeneficiaryClick(coben.id, $event)">
                {{ coben.name }}
              </v-chip> -->
            </div>
          </div>

          <div class="box-container box-w3 mt-5">
            <h3 class="pa-2 d-flex justify-space-between align-center">
              <div><v-icon>mdi-file-download</v-icon> Course Files</div>
              <div
                class="total-badge rounded d-flex align-center justify-center"
              >
                {{ course.courseFiles.length }}
              </div>
            </h3>
            <div
              v-for="courseFile in course.courseFiles"
              :key="courseFile.id"
              class="pa-2 course-file-item"
            >
              <v-chip> {{ removeTimestamp(courseFile.fileName) }} </v-chip>
              <v-tooltip v-if="courseFile.notes" interactive>
                <template #activator="{ props: activatorProps }">
                  <v-icon
                    icon="mdi-information-variant-circle"
                    color="academy"
                    v-bind="activatorProps"
                  />
                </template>
                <div>
                  {{ courseFile.notes }}
                </div>
              </v-tooltip>
              <v-icon
                icon="mdi-download"
                class="align-content-end"
                @click="fetchCourseFile(courseFile)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h1 {
  font-size: 48px;
  color: rgb(var(--v-theme-academy));
}
.box-holder {
  gap: 20px;
  .box-row {
    flex-direction: column;
    .box-w3 {
      width: 100%;
    }
    .box-container {
      border: 1px solid rgb(var(--v-theme-academy));
      .total-badge {
        background: white;
        color: rgb(var(--v-theme-academy));
        font-size: 14px;
        width: 25px;
        height: 25px;
      }
      h3 {
        background-color: rgb(var(--v-theme-academy));
        color: white;
      }
      .info {
        .label {
          text-decoration: underline;
        }
        .answer {
          display: block;
          font-weight: bold;
        }
      }
      .course-file-item {
        display: flex;
        align-items: center;

        .mdi-download {
          margin-left: auto;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
